import { Empty, Flex, Grid, Skeleton, Table, Typography } from 'antd'
import type { ColumnType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import { formatDateTime, formatNumber } from '@/shared/lib/format'
import { LoadError } from '@/shared/ui/LoadError'
import { VoucherAwaiting } from './VoucherAwaiting'
import type { ActivationsState } from './useVoucherStock'
import {
  isEndpointMissing,
  MY_ACTIVATIONS_ENDPOINT,
  type VoucherActivationRow,
} from './voucherStockApi'

interface MyActivationsListProps {
  state: ActivationsState
  /** Paketning foydalanuvchi tilidagi nomi; `null` — nom topilmadi */
  planLabel: (row: VoucherActivationRow) => string | null
}

/**
 * «Men ishlatgan kartalar».
 *
 * <h2>⛔ KOD BU YERDA OCHIQ — va bu himoyaning buzilishi EMAS</h2>
 * Ro'yxatning HAR qatori allaqachon ishlatilgan kartaga tegishli
 * (endpoint ta'rifi shu), ya'ni kodni ko'rgan odam u bilan hech narsa
 * qila olmaydi. Berkitish esa aynan yagona foydali holatni yopardi:
 * mijoz «kartam ishlamadi» deb kelganda sherik shu ro'yxatdan kodni
 * topib, qachon va kimga ketganini ko'rsatadi.
 *
 * ⚠️ «Kartalarim» dagi kod BERKITILADI, bu yerda YO'Q — farq holatda,
 * ekranda emas (`voucherCode.ts` dagi qoida).
 *
 * <h2>⚠️ BU RO'YXAT «PARTIYALARIM» BILAN KESISHMASLIGI MUMKIN</h2>
 * Sherik O'ZIGA berilmagan partiyaning kartasini ham faollashtira
 * oladi: `voucher.activate` ikkala `PartnerKind` to'plamida va u
 * partiyaning `agent_id` iga UMUMAN qaramaydi. Ya'ni bu yerdagi qator
 * birinchi bo'limda topilmasligi normal holat — «yo'qolgan ma'lumot»
 * emas.
 */
export function MyActivationsList({ state, planLabel }: MyActivationsListProps) {
  const { t } = useTranslation()
  const screens = Grid.useBreakpoint()
  const isMobile = screens.md === false

  if (state.error && isEndpointMissing(state.error)) {
    return (
      <VoucherAwaiting
        endpoint={MY_ACTIVATIONS_ENDPOINT}
        note={t('myVouchers.awaitingActivations')}
      />
    )
  }
  if (state.error) {
    return <LoadError titleKey="myVouchers.activationsFailed" error={state.error} />
  }
  if (state.loading) {
    return <Skeleton active paragraph={{ rows: 4 }} />
  }

  const rows = state.page?.rows ?? []
  if (rows.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t('myVouchers.activationsEmpty')}
      >
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          {t('myVouchers.activationsEmptyHint')}
        </Typography.Text>
      </Empty>
    )
  }

  const codeText = (row: VoucherActivationRow) => (
    <Typography.Text copyable style={{ fontFamily: 'monospace', letterSpacing: 0.5 }}>
      {row.code}
    </Typography.Text>
  )

  if (isMobile) {
    return (
      <Flex vertical gap={8}>
        {rows.map((row) => (
          <div
            key={row.voucherId}
            style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', paddingBottom: 8 }}
          >
            <Flex justify="space-between" align="baseline" gap={8}>
              {codeText(row)}
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {formatDateTime(row.activatedAt)}
              </Typography.Text>
            </Flex>
            {/* ⚠️ Login `null` bo'lishi MUMKIN va bu nosozlik emas — eski
                yozuvda u saqlanmagan. Chiziqcha «noma'lum abonent» degan
                yolg'on da'vodan afzal (`OperationsList` dagi ayni qaror). */}
            <div style={{ fontSize: 13 }}>{row.subscriberLogin ?? t('common.dash')}</div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {planLabel(row) ?? t('common.dash')}
            </Typography.Text>
          </div>
        ))}
      </Flex>
    )
  }

  const columns: ColumnType<VoucherActivationRow>[] = [
    {
      title: t('myVouchers.col.activatedAt'),
      render: (_v, row) => formatDateTime(row.activatedAt),
      width: 170,
    },
    { title: t('myVouchers.col.code'), render: (_v, row) => codeText(row), width: 180 },
    {
      title: t('myVouchers.col.batchNo'),
      render: (_v, row) => (row.batchNo === null ? t('common.dash') : `#${formatNumber(row.batchNo)}`),
      width: 90,
    },
    {
      title: t('myVouchers.col.plan'),
      render: (_v, row) => planLabel(row) ?? t('common.dash'),
      ellipsis: true,
    },
    {
      title: t('myVouchers.col.subscriber'),
      render: (_v, row) => row.subscriberLogin ?? t('common.dash'),
      ellipsis: true,
    },
    {
      // ⛔ Kartaning AKTIVLASHTIRILGANDAN keyingi muddati — partiyaning
      // `validUntil` i EMAS. `null` — plan muddat bermagan va bu
      // «cheksiz» degani EMAS, shuning uchun chiziqcha (`voucherApi.ts`
      // dagi `expiresAt` izohi).
      title: t('myVouchers.col.expiresAt'),
      render: (_v, row) => (row.expiresAt ? formatDateTime(row.expiresAt) : t('common.dash')),
      width: 170,
    },
  ]

  return (
    <Table<VoucherActivationRow>
      dataSource={rows}
      rowKey={(row) => row.voucherId}
      pagination={false}
      size="small"
      scroll={{ x: true }}
      columns={columns}
    />
  )
}
